import React, { useEffect } from 'react'

import { IconLink } from 'components/ui/icons'

import { Stop, Tour, Mood } from 'generated/mos/org/getty/audio'

import { TMSObject } from 'generated/mos/org/getty/integrationtms'

import { space } from 'helpers/style'
import { timestampToFormattedString } from 'helpers/timestamps'
import { Space, Site } from 'generated/mos/structure'
import { Placement, SpacePlacement } from 'generated/mos/placement'
import { statusSelector } from 'helpers/status'

import styled from 'styled'

import { tourSchema } from '../schemas/tour'
import { moodSchema } from '../schemas/mood'

const findSpaceForEntity = (entity: any, spaces?: ReadonlyArray<Space.Entity>): Space.Entity | undefined => {
  if (!spaces || !entity.placement || !entity.placement.placement || !entity.placement.placement.spaceRef) return
  if (entity.placement.type !== Placement.refName || entity.placement.placement.type !== SpacePlacement.refName) return

  return spaces.find((item: Space.Entity) => entity.placement.placement.spaceRef.id === Space.mustRef(item.ref).id)
}

const resolveSiteName = (sitesData: any, siteRef: Site.Ref) => {
  const site = sitesData && sitesData.sites && sitesData.sites.find((site: any) => Site.mustRef(site.ref).id === siteRef.id)
  if (!site) return ''
  return site.name
};

export const getTMSPlacement = (entity: any, sites: any, spaces?: ReadonlyArray<Space.Entity>): string => {
  const resolved = findSpaceForEntity(entity, spaces)
  if (!resolved || !sites || !resolved.siteRef) return ''

  const siteName = resolveSiteName(statusSelector.data(sites), resolved.siteRef)
  const name = resolved.displayName ? resolved.displayName : resolved.name

  return `${siteName} - ${name}`
}


const MoodList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
`

const DetailsContainer = styled.div`
  padding: ${space(5)};
  border: 1px solid ${({ theme }) => theme.color.grayL70};
  border-bottom: 0;
  color: ${({ theme }) => theme.color.gray};
  font-size: ${({ theme }) => theme.font.size.small};
  font-weight: ${({ theme  }) => theme.font.weight.light};

  &:first-of-type {
    border-top-left-radius: 2px;
    border-top-right-radius: 2px;
  }

  &:last-of-type {
    border-bottom: 1px solid ${({ theme }) => theme.color.grayL70};
    border-bottom-left-radius: 2px;
    border-bottom-right-radius: 2px;
  }
`

const DetailsHeading = styled.h4`
  margin-top: 0;
  font-size: ${({ theme }) => theme.font.size.small};
  font-weight: ${({ theme  }) => theme.font.weight.base};
`

const LinkedFieldIcon = styled(IconLink)`
  display: inline-block;
  vertical-align: middle;
`

const SyncDate = styled.p`
  margin-top: 0;
  font-size: ${({ theme }) => theme.font.size.xSmall};
`

const DescriptionList = styled.dl`
  display: flex;
  flex-wrap: wrap;
  margin: 0;
`

const DescriptionTitle = styled.dt`
  padding-bottom: ${space(1)};
  width: 130px;
`

const DescriptionValue = styled.dd`
  margin-left: 0;
  padding-bottom: ${space(1)};
  width: calc(100% - 130px)
`

const TourList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
`

const Badge = styled.div`
  display: inline-block;
  margin-top: ${space(2)};
  padding: 1px 2px;
  border: 1px solid ${({ theme }) => theme.color.grayL20};
  border-radius: 2px;
  font-size: 10px;
  font-weight: ${({ theme  }) => theme.font.weight.light};
  text-transform: uppercase;
`

type TmsDetailsProps = {
  readonly tmsObject: TMSObject.Entity | undefined;
  readonly sites: any;
  readonly spacesListData?: ReadonlyArray<Space.Entity>;

  readonly stop?: Stop.Entity;
  readonly moods?: ReadonlyArray<Mood.Entity>;
  readonly tours?: ReadonlyArray<Tour.Entity>;
};

//             {<TmsDetails tmsObject={baseEntity} sites={sites} spacesListData={spacesListData}/> : null}
//             {stop && <StopDetails stop={stop} tours={tours} />}

export const TmsDetails = ({ tmsObject, sites, spacesListData, stop, tours, moods }: TmsDetailsProps) => {
    if (!tmsObject) return null
    const placement = getTMSPlacement(tmsObject, sites, spacesListData)
    const stopInfo = (stop && <DetailsContainer>
      <DetailsHeading>Stop Info</DetailsHeading>
      <DescriptionList>
        {stop.metadata && stop.metadata.modifiedAt && (
        <>
          <DescriptionTitle>Modified</DescriptionTitle>
          <DescriptionValue>
            {timestampToFormattedString(stop.metadata.modifiedAt.value)}
          </DescriptionValue>
        </>
            )}
        {stop.metadata && stop.metadata.createdAt && (
        <>
          <DescriptionTitle>Created</DescriptionTitle>
          <DescriptionValue>
            {timestampToFormattedString(stop.metadata.createdAt.value)}
          </DescriptionValue>
        </>
            )}
        {tours && tours.length ? (<>
          <DescriptionTitle>In Tours</DescriptionTitle>
          <DescriptionValue>
            <TourList>
              {tours.map(tour => (
                <li key={tour.ref && tour.ref.id}>
                  {tourSchema.name.read(tour.name)}
                </li>
                  ))}
            </TourList>
          </DescriptionValue>
        </>) : null}
        {moods && moods.length ? (<>
          <DescriptionTitle>In Moods</DescriptionTitle>
          <DescriptionValue>
            <MoodList>
              {moods.map(mood => (
                <li key={mood.ref && mood.ref.id}>
                  {moodSchema.name.read(mood.name)}
                </li>
                  ))}
            </MoodList>
          </DescriptionValue>
        </>) : null}

      </DescriptionList>
    </DetailsContainer>
	)

    return (
      <>
        <DetailsContainer>
          <DetailsHeading>
            <LinkedFieldIcon size="base" />
              TMS Object
          </DetailsHeading>
          {tmsObject.modifiedAt && (
            <SyncDate>
              Last sync: {timestampToFormattedString(tmsObject.modifiedAt.value)}
            </SyncDate>
          )}
          <DescriptionList>
            <DescriptionTitle>Accession No.</DescriptionTitle>
            <DescriptionValue>{tmsObject.accessionNumber}</DescriptionValue>
            <DescriptionTitle>Primary Title</DescriptionTitle>
            <DescriptionValue>{tmsObject.primaryTitle}</DescriptionValue>
            <DescriptionTitle>TMS ID</DescriptionTitle>
            <DescriptionValue>{tmsObject.tmsId}</DescriptionValue>
            {placement ? (
              <>
                <DescriptionTitle>TMS Location</DescriptionTitle>
                <DescriptionValue>
                  {placement}
                  <div>
                    <Badge>On display</Badge>
                  </div>
                </DescriptionValue>
              </>
            ) : null}
          </DescriptionList>
        </DetailsContainer>
        {stopInfo}
      </>
    )
}
